import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Mail, ArrowRight, ArrowLeft, MailCheck } from "lucide-react";
import { Logo } from "@/components/brand/logo";
import { z } from "zod";
import { AuthLayout } from "@/components/auth/auth-layout";
import { AuthField } from "@/components/auth/auth-field";
import { LoadingButton } from "@/components/auth/loading-button";

const schema = z.object({
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email").max(255),
});

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset password — Spayzo" },
      { name: "description", content: "Send yourself a secure link to reset your Spayzo password." },
    ],
  }),
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse({ email });
    if (!result.success) {
      setError(result.error.issues[0]?.message);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitting(false);
    setSent(true);
  };

  return (
    <AuthLayout side="right" hideFooter>
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        {!sent ? (
          <>
            <Logo to="/" className="mb-2 h-8 w-auto text-foreground" />
            <div className="mb-8 mt-3">
              <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                {t("auth.resetTitle")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("auth.resetSubtitle")}
              </p>
            </div>

            <form onSubmit={onSubmit} noValidate className="space-y-4">
              <AuthField
                label={t("common.email")}
                type="email"
                icon={Mail}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(undefined);
                }}
                error={error}
              />

              <LoadingButton
                type="submit"
                loading={submitting}
                className="mt-2 h-12 w-full rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-95 active:scale-[0.99] disabled:opacity-70"
              >
                {t("auth.sendReset")} <ArrowRight className="h-4 w-4" />
              </LoadingButton>
            </form>

            <p className="mt-8 text-center text-xs text-muted-foreground">
              {t("auth.remembered")}{" "}
              <Link to="/login" className="font-medium text-primary hover:underline">
                {t("auth.signInInstead")}
              </Link>
            </p>
          </>
        ) : (
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MailCheck className="h-7 w-7" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">{t("auth.checkInbox")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("auth.resetSent")}{" "}
              <span className="font-medium text-foreground">{email}</span>. {t("auth.resetExpires")}
            </p>

            <div className="mt-8 space-y-3">
              <button
                type="button"
                onClick={() => setSent(false)}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium transition-colors hover:bg-muted"
              >
                {t("auth.useDifferentEmail")}
              </button>
              <Link
                to="/login"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> {t("auth.backToSignIn")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
